import { useMemo, useState } from 'react';
import type { CellValueChangedEvent, ColDef, ICellRendererParams } from 'ag-grid-community';
import { NJButton, NJIconButton, NJInlineMessage, NJTooltip } from '@engie-group/fluid-design-system-react';
import DataTable from '@shared/DataTable';
import { Size2MixAccordion, Size2MixExpandCollapseBar, Size2MixSection, useSectionsToggle } from '@shared/size2mix-shell';
import { DeleteConfirmModal, EditDeleteActions, TableTitle, formatValue, nemoFlexWidths, parseNonNegative } from '../ref-production-fuels/ui';
import '../ref-production-fuels/contracts.css';
import { useElectricityMutation } from './hooks';
import PowerContractPanel from './PowerContractPanel';
import type { ElectricityData, PowerContract } from './types';

const SECTIONS = ['electricity'] as const;
const TABLE_DEFAULTS: ColDef = { flex: 1, resizable: true, sortable: true, wrapHeaderText: true, autoHeaderHeight: true };
const READONLY = 'production-contracts-readonly production-contracts-center';
const EDITABLE = 'production-contracts-editable';

export default function ElectricityPage({ data }: { data: ElectricityData }) {
    const { bind, expandAll, collapseAll } = useSectionsToggle(SECTIONS);
    const mutation = useElectricityMutation();
    const [panel, setPanel] = useState<{ item?: PowerContract } | null>(null);
    const [deleting, setDeleting] = useState<PowerContract | null>(null);
    const contracts = data.powerContracts;

    const closePanel = () => { setPanel(null); mutation.reset(); };
    const tableError = !panel && !deleting && mutation.isError ? mutation.error.message : undefined;

    const columns = useMemo<ColDef<PowerContract>[]>(() => [
        { field: 'name', headerName: 'Nom', cellClass: 'production-contracts-readonly', filter: true, flex: 3 },
        { field: 'linkedSites', headerName: 'Sites liés', cellClass: READONLY, flex: 2 },
        { field: 'linkedUnits', headerName: 'Équipements de production liés', cellClass: READONLY, flex: 2 },
        { field: 'linkedPumpGroups', headerName: 'Groupes de pompes liés', cellClass: READONLY, flex: 2 },
        { field: 'fixedPrice', headerName: 'Prix fixe (€/J)', editable: true, cellClass: `${EDITABLE} production-contracts-center`, flex: 2, valueParser: parseNonNegative, valueFormatter: ({ value }) => formatValue(value) },
        {
            headerName: 'Prix variable (€/J)',
            flex: 2,
            sortable: false,
            cellClass: 'production-contracts-center',
            // Production `DisabledVariablePriceCellRenderer`: the icon is always disabled.
            cellRenderer: ({ data: row }: ICellRendererParams<PowerContract>) => row && (
                <NJTooltip text="Prix variable" hasArrow placement="top">
                    <NJIconButton icon="open_in_new" label="" aria-label="Prix variable" variant="tertiary" isDisabled />
                </NJTooltip>
            ),
        },
        { field: 'description', headerName: 'Description', editable: true, cellClass: EDITABLE, flex: 3, valueFormatter: ({ value }) => formatValue(value) },
        {
            headerName: 'Actions',
            flex: 2,
            sortable: false,
            cellRenderer: ({ data: row }: ICellRendererParams<PowerContract>) => row && (
                <EditDeleteActions label={row.name} onEdit={() => setPanel({ item: row })} onDelete={() => setDeleting(row)} />
            ),
        },
    ], []);

    const onChange = (event: CellValueChangedEvent<PowerContract>) => {
        const field = event.colDef.field;
        if (!field || event.newValue === event.oldValue) return;
        mutation.mutate({ kind: 'update', id: event.data.id, patch: { [field]: event.newValue ?? (field === 'description' ? '' : null) } });
    };

    return (
        <div className="production-contracts">
            {tableError && <NJInlineMessage variant="error" title="Modification impossible">{tableError}</NJInlineMessage>}
            <Size2MixAccordion>
                <Size2MixExpandCollapseBar onExpandAll={expandAll} onCollapseAll={collapseAll} />
                <Size2MixSection togglePosition="trailing" title={<span>Électricité</span>} icon="bolt" {...bind('electricity')}>
                    <div className="production-contracts-body">
                        <div><NJButton label="Créer un nouveau contrat" icon="add" onClick={() => setPanel({})} /></div>
                        {contracts.length === 0 ? (
                            <NJInlineMessage variant="information">Tableau indisponible : aucun contrat électrique n'a été paramétré pour ce réseau.</NJInlineMessage>
                        ) : (
                            <div>
                                <TableTitle title="Contrats d'électricité" />
                                <DataTable<PowerContract> testId="power-contracts-table" header={columns} data={contracts} isPaginated paginationPageSize={15} defaultColDef={TABLE_DEFAULTS} onGridReady={nemoFlexWidths} onCellValueChanged={onChange} />
                            </div>
                        )}
                    </div>
                </Size2MixSection>
            </Size2MixAccordion>

            {panel && (
                <PowerContractPanel
                    contract={panel.item}
                    existingNames={contracts.map((c) => c.name)}
                    error={mutation.isError ? mutation.error.message : undefined}
                    isPending={mutation.isPending}
                    onClose={closePanel}
                    onSubmit={(value) => (panel.item
                        ? mutation.mutate({ kind: 'update', id: panel.item.id, patch: value }, { onSuccess: closePanel })
                        : mutation.mutate({ kind: 'create', input: value }, { onSuccess: closePanel }))}
                />
            )}
            {deleting && (
                <DeleteConfirmModal
                    id="delete-power-contract"
                    isPending={mutation.isPending}
                    onCancel={() => setDeleting(null)}
                    onConfirm={() => mutation.mutate({ kind: 'delete', id: deleting.id }, { onSettled: () => setDeleting(null) })}
                >
                    Êtes-vous sûr de vouloir supprimer le contrat électrique <strong>{deleting.name}</strong> ?
                </DeleteConfirmModal>
            )}
        </div>
    );
}
